import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { createContext, useCallback, useEffect, useState } from 'react';
import {
  useMigrationToSNModalActions,
  useResetAllStoredData,
  useSession,
} from 'src/hooks';
import { useLazyGetUserByIdQuery, usersApi } from 'src/store/services';
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';
import log from 'src/utils/logger';
import { INVITATION_TOKEN } from 'src/constants';
import { useLogout } from 'src/hooks/useLogout';
import { useQueryParams } from 'src/hooks';
import { UserTier } from 'src/types';
import { useChoosePlanModal } from 'src/hooks/useChoosePlanModal';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { saveLastLogin } from 'src/utils/lastLoginStorage';
import { SSOProvider, EMAIL_LOGIN_PROVIDER } from 'src/types';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { env } from 'src/env';
import posthog from 'posthog-js';
import useSafeLogger from 'src/hooks/useSafeLogger';

// cookie to store URL from SuperNinja
const SUPER_NINJA_RETURN_URL_COOKIE_NAME = '_superNinjaReturnUrl';

// sessionStorage key to store OAuth provider during sign-in redirect
export const OAUTH_PROVIDER_SESSION_KEY = 'ninja_oauth_provider';

export type AuthContextType = {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isGuestAccess: boolean;
  shouldFetchUser: boolean;
  userId: string | undefined;
  sessionId: string | null | undefined;
  setAuthLoading: (value: boolean) => void;
};

type AuthContextProviderProps = {
  children: React.ReactNode;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isAuthLoading: true,
  isGuestAccess: true,
  shouldFetchUser: true,
  userId: undefined,
  sessionId: undefined,
  setAuthLoading: () => undefined,
});

const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const { pgLogError, pgLogInfo } = useSafeLogger();

  const [isAuthenticated, setAuthenticated] = useState(false);
  const [isAuthLoading, setAuthLoading] = useState(true);
  const [isGuestAccess, setGuestAccess] = useState(false);
  const [shouldFetchUser, setShouldFetchUser] = useState(true);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [sessionId, setSessionId] = useState<string | null | undefined>(
    undefined,
  );
  const { appUser } = useSession();
  const { logout } = useLogout();
  const [triggerGetUserById] = useLazyGetUserByIdQuery();

  const { removeSearchParam } = useQueryParams();
  const { onResetAllStoredData } = useResetAllStoredData();

  const { shouldShowPlanModal, changeShouldShowPlanModal } =
    useChoosePlanModal();

  const { changeShouldShowMigrationToSNModal } = useMigrationToSNModalActions();
  const disableRequestsInMNForFreeTier = useFeatureFlagEnabled(
    'disableRequestsInMNForFreeTier',
  );
  const enableHandlingOfBusinessLogicInSN =
    useFeatureFlagEnabled('handleBusinessInSN');

  const invitationToken = localStorage.getItem(INVITATION_TOKEN);

  const [updateUserMutation] = usersApi.useUpdateUserMutation();

  const handleSessionId = useCallback((reuseExisting = false) => {
    let wsSessionId;
    if (reuseExisting) {
      wsSessionId =
        window.sessionStorage.getItem('ninja_ws_session_id') || uuidv4();
    } else {
      wsSessionId = uuidv4();
    }
    window.sessionStorage.setItem('ninja_ws_session_id', wsSessionId);
    setSessionId(wsSessionId);
  }, []);

  const clearSessionId = useCallback(() => {
    window.sessionStorage.removeItem('ninja_ws_session_id');
    setSessionId(undefined);
  }, []);

  // Check authentication status without creating guest users
  useEffect(() => {
    const checkAuthenticated = async () => {
      log.info('[AuthContext] Starting authentication check');
      const authSession = await fetchAuthSession();
      if (authSession.tokens) {
        const extractedUserId = authSession.tokens.accessToken.payload[
          'custom:ninja_user_id'
        ] as string | undefined;

        if (!extractedUserId) {
          log.error(
            '[AuthContext] Auth tokens found but custom:ninja_user_id is missing from token payload',
          );
          setAuthenticated(false);
          setGuestAccess(true);
          setShouldFetchUser(false);
          setAuthLoading(false);
          return;
        }

        log.info(
          '[AuthContext] Valid auth tokens found, setting authenticated state',
        );
        setAuthenticated(true);
        setGuestAccess(false);
        setUserId(extractedUserId);
        handleSessionId(true);
        setAuthLoading(false);
        log.info('[AuthContext] Authentication state updated successfully', {
          userId: extractedUserId,
          isAuthenticated: true,
        });
      } else {
        log.info(
          '[AuthContext] No valid auth tokens found, user is not authenticated',
        );
        if (appUser?.tier_id) {
          log.warn(
            '[AuthContext] User data still exists in store despite no auth tokens, clearing stored data',
            {
              userId: appUser.user_id,
              tierID: appUser.tier_id,
            },
          );
          await onResetAllStoredData();
        }
        setAuthenticated(false);
        setGuestAccess(true);
        setShouldFetchUser(false);
        setAuthLoading(false);
        log.info(
          '[AuthContext] Guest access state set, user remains on current page',
        );
      }
    };

    checkAuthenticated();

    const updateUserDataAfterLogin = async () => {
      log.info('[AuthContext] Starting post-login user data update');
      setUserId(undefined);

      await onResetAllStoredData();
      log.info('[AuthContext] Stored data reset completed');

      handleSessionId();

      const authSession = await fetchAuthSession();
      if (authSession.tokens) {
        setAuthenticated(true);
        setGuestAccess(false);
        setUserId(
          authSession.tokens?.accessToken.payload[
            'custom:ninja_user_id'
          ] as string,
        );
        setAuthLoading(false);
        log.info('[AuthContext] User authenticated after login', { userId });
      }
      try {
        const { data: userData } = await triggerGetUserById(
          authSession.tokens?.accessToken.payload[
            'custom:ninja_user_id'
          ] as string,
          true,
        );
        posthog.capture('User logged-in at SuperNinja', {
          email: userData?.email,
          tierId: userData?.tier_id,
        });
        if (userData?.user_id) {
          // Save last login info
          try {
            const oauthProvider = sessionStorage.getItem(
              OAUTH_PROVIDER_SESSION_KEY,
            );
            const email = userData.email;

            if (email) {
              if (oauthProvider) {
                // OAuth login - validate provider is a valid SSOProvider
                const validProviders = Object.values(SSOProvider);
                if (validProviders.includes(oauthProvider as SSOProvider)) {
                  saveLastLogin(email, oauthProvider as SSOProvider);
                }
                // Clear the sessionStorage after use
                sessionStorage.removeItem(OAUTH_PROVIDER_SESSION_KEY);
              } else {
                // Email login
                saveLastLogin(email, EMAIL_LOGIN_PROVIDER);
              }
            }
          } catch (e) {
            log.error('Failed to save last login info', e);
          }

          if (
            userData?.tier_id === UserTier.FREE &&
            disableRequestsInMNForFreeTier &&
            !invitationToken
          ) {
            changeShouldShowMigrationToSNModal(true);
          }

          if (
            userData?.tier_id === UserTier.FREE &&
            !shouldShowPlanModal &&
            !disableRequestsInMNForFreeTier
          ) {
            changeShouldShowPlanModal(true);
          }

          // store logged-in user's fingerprint if not already stored
          if (!userData.fingerprint) {
            try {
              const fingerprint = await getFingerprint();
              // user mutation directly because useUserData hook somehow messed the state when I tried to modify user here
              await updateUserMutation({
                id: userData?.user_id,
                body: {
                  fingerprint: fingerprint as string,
                },
              }).unwrap();
            } catch (e) {
              pgLogError("Failed to store user's fingerprint", {
                user_id: appUser?.user_id,
              });
            }
          }
        }
      } catch (e) {
        log.error('Unable to get user and budget info to show banners', e);
      }
    };

    const updateDataAfterLogout = async () => {
      log.info('[AuthContext] Starting logout cleanup');
      try {
        setUserId(undefined);
        setAuthenticated(false);
        setGuestAccess(true);
        setShouldFetchUser(false);
        clearSessionId();
        setAuthLoading(false);
        log.info('[AuthContext] Logout cleanup completed successfully');
      } catch (e) {
        log.error('[AuthContext] Error during logout cleanup:', e);
        setAuthLoading(false);
      }
    };

    const stopListen = Hub.listen('auth', (data) => {
      const event = data?.payload?.event;

      pgLogInfo(event, {
        user_id: appUser?.user_id,
      });

      switch (event) {
        case 'signedIn':
          updateUserDataAfterLogin().then(() => {
            // if SuperNinja return cookie is set, redirect user back
            const _returnUrl = Cookies.get(SUPER_NINJA_RETURN_URL_COOKIE_NAME);
            if (_returnUrl) {
              Cookies.remove(SUPER_NINJA_RETURN_URL_COOKIE_NAME, {
                domain: window.location.hostname,
              });
              window.location.href = _returnUrl;
              return;
            }
            if (enableHandlingOfBusinessLogicInSN) {
              window.location.href = env.REACT_APP_SUPER_NINJA_URL;
              return;
            }
            removeSearchParam('code');
            removeSearchParam('state');
          });
          return;
        case 'signInWithRedirect_failure':
          setAuthenticated(false);
          setAuthLoading(false);
          pgLogError('Sign in failure', {
            user_id: appUser?.user_id,
          });
          window.location.reload();
          return;
        case 'tokenRefresh_failure':
          logout();
          return;
        case 'signedOut': {
          updateDataAfterLogout();
          return;
        }
        default:
          return;
      }
    });

    return () => {
      stopListen();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthLoading,
        setAuthLoading,
        isGuestAccess,
        shouldFetchUser,
        userId,
        sessionId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContextProvider };
export default AuthContext;
